import axios from "axios";
import { config } from "../../_config/config";
import tokenService from "../../_services/token.service";
import {
  AdminCompanyExtended,
  AdminOffer,
  CandidateRow,
  AdminCandidateDetail,
  AdminCandidateListRow,
} from "./admin-types";

const baseUrl = config.SERVER_URL;

const getBody = <T>(data: any): T => (data && data.body ? data.body : data);
const DEFAULT_PAGE_SIZE = 25;

export interface AdminListQuery {
  page?: number;
  size?: number;
  search?: string;
  orderBy?: string;
}

export interface AdminPaginatedResult<T> {
  rows: T[];
  page: number;
  size: number;
  total: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

const toPositiveNumber = (value: any): number | null => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
};

const parsePaginatedBody = <T>(
  body: any,
  requestedPage: number,
  requestedSize: number
): AdminPaginatedResult<T> => {
  const rows: T[] = Array.isArray(body?.data) ? body.data : [];

  const size =
    toPositiveNumber(body?.size) ||
    toPositiveNumber(body?.pageSize) ||
    requestedSize;
  const page =
    toPositiveNumber(body?.currentPage) ||
    toPositiveNumber(body?.page) ||
    requestedPage;

  const total =
    toPositiveNumber(body?.totalElements) ||
    toPositiveNumber(body?.count) ||
    toPositiveNumber(body?.total) ||
    toPositiveNumber(body?.pagination?.total) ||
    toPositiveNumber(body?.meta?.total) ||
    rows.length;

  const hasNext =
    body?.links?.next?.number !== null && body?.links?.next?.number !== undefined
      ? true
      : Boolean(body?.next) || page * size < total;
  const hasPrevious =
    body?.links?.previous?.number !== null &&
    body?.links?.previous?.number !== undefined
      ? true
      : Boolean(body?.previous) || page > 1;

  return {
    rows,
    page,
    size,
    total,
    hasNext,
    hasPrevious,
  };
};

async function getAuthed<T>(
  path: string,
  params?: Record<string, any>
): Promise<T> {
  const token = tokenService.getLocalAccessToken();
  const response = await axios.get(baseUrl + path, {
    headers: { Authorization: "Bearer " + token },
    params,
  });
  return response.data;
}

async function postAuthed<T>(path: string, data?: Record<string, any>): Promise<T> {
  const token = tokenService.getLocalAccessToken();
  const response = await axios.post(baseUrl + path, data, {
    headers: { Authorization: "Bearer " + token },
  });
  return response.data;
}

export const adminService = {
  async fetchCompanies(
    query: AdminListQuery = {}
  ): Promise<AdminPaginatedResult<AdminCompanyExtended>> {
    const page = Math.max(1, query.page || 1);
    const size = Math.max(1, query.size || DEFAULT_PAGE_SIZE);
    const params: Record<string, any> = { page, size };
    if (query.search?.trim()) params.search = query.search.trim();
    if (query.orderBy) params.order_by = query.orderBy;

    const raw = await getAuthed<any>("company/", params);
    const body = getBody<any>(raw);
    return parsePaginatedBody<AdminCompanyExtended>(body, page, size);
  },

  async fetchCompanyOffers(companyId: string): Promise<AdminOffer[]> {
    const size = 100;
    let page = 1;
    let hasNext = true;
    const rows: AdminOffer[] = [];

    while (hasNext) {
      const raw = await getAuthed<any>(`offer/company/${companyId}/`, {
        size,
        page,
      });
      const body = getBody<any>(raw);
      const parsed = parsePaginatedBody<AdminOffer>(body, page, size);
      rows.push(...(parsed.rows || []));
      hasNext = parsed.hasNext;
      page += 1;
    }

    return rows;
  },

  async fetchAllOffersForAdmin(
    query: AdminListQuery = {}
  ): Promise<AdminPaginatedResult<AdminOffer>> {
    const page = Math.max(1, query.page || 1);
    const size = Math.max(1, query.size || DEFAULT_PAGE_SIZE);
    const params: Record<string, any> = { admin_all: 1, page, size };
    if (query.search?.trim()) params.search = query.search.trim();
    if (query.orderBy) params.order_by = query.orderBy;

    const raw = await getAuthed<any>("offer/", params);
    const body = getBody<any>(raw);
    return parsePaginatedBody<AdminOffer>(body, page, size);
  },

  async fetchCompany(companyId: string): Promise<AdminCompanyExtended | null> {
    const raw = await getAuthed<any>(`company/${companyId}/`);
    const body = getBody<any>(raw);
    return body || null;
  },

  async fetchOfferCandidates(offerId: string): Promise<CandidateRow[]> {
    const raw = await getAuthed<any>(`offer/${offerId}/candidates`, {
      size: 50,
    });
    return raw.body || [];
  },

  async fetchCandidates(
    query: AdminListQuery = {}
  ): Promise<AdminPaginatedResult<AdminCandidateListRow>> {
    const page = Math.max(1, query.page || 1);
    const size = Math.max(1, query.size || DEFAULT_PAGE_SIZE);
    const params: Record<string, any> = { page, size };
    if (query.search?.trim()) params.search = query.search.trim();
    if (query.orderBy) params.order_by = query.orderBy;

    const raw = await getAuthed<any>("candidate/", params);
    const body = getBody<any>(raw);
    return parsePaginatedBody<AdminCandidateListRow>(body, page, size);
  },

  async fetchCandidate(
    candidateId: string
  ): Promise<AdminCandidateDetail | null> {
    const raw = await getAuthed<any>(`candidate/${candidateId}`);
    const body = getBody<any>(raw);
    return body || null;
  },

  async setCandidatePassword(candidateId: string, password: string): Promise<void> {
    await postAuthed<any>(`candidate/${candidateId}/set-password/`, {
      password,
    });
  },

  async setCompanyPassword(companyId: string, password: string): Promise<void> {
    await postAuthed<any>(`company/${companyId}/set-password/`, {
      password,
    });
  },

  async fetchSalesReps(): Promise<any[]> {
    return await getAuthed<any>("referrals/reps/");
  },

  async fetchSalesRep(id: number): Promise<any> {
      return await getAuthed<any>(`referrals/reps/${id}/`);
  },

  async createSalesRep(data: { name: string; email: string }): Promise<any> {
    const token = tokenService.getLocalAccessToken();
    const response = await axios.post(baseUrl + "referrals/reps/", data, {
      headers: { Authorization: "Bearer " + token },
    });
    return response.data;
  },

  async createReferralCode(salesRepId: number, data: any): Promise<any> {
    const token = tokenService.getLocalAccessToken();
    // We create code via ViewSet but linked to Rep.
    // Actually the ViewSet is flat 'referrals/codes/'. We need to pass sales_rep ID.
    const payload = { ...data, sales_rep: salesRepId };
    const response = await axios.post(baseUrl + "referrals/codes/", payload, {
      headers: { Authorization: "Bearer " + token },
    });
    return response.data;
  },

  async updateReferralCode(codeId: number, data: { discount_percent?: number; bonus_months?: number; bonus_matches?: number }): Promise<any> {
    const token = tokenService.getLocalAccessToken();
    const response = await axios.patch(baseUrl + `referrals/codes/${codeId}/`, data, {
      headers: { Authorization: "Bearer " + token },
    });
    return response.data;
  },

  async fetchSalesRepSignups(repId: number): Promise<any[]> {
    return await getAuthed<any[]>(`referrals/reps/${repId}/signups/`);
  },

  async sendQrCode(codeId: number): Promise<any> {
    const token = tokenService.getLocalAccessToken();
    const response = await axios.post(
      baseUrl + `referrals/codes/${codeId}/send_email/`,
      {},
      {
        headers: { Authorization: "Bearer " + token },
      }
    );
    return response.data;
  },

  async fetchPromoCodes(): Promise<any[]> {
    return await getAuthed<any>("promo-codes/");
  },

  async createPromoCode(data: {
    code: string;
    discount_percent: number;
    bonus_months: number;
    bonus_matches: number;
  }): Promise<any> {
    const token = tokenService.getLocalAccessToken();
    const response = await axios.post(baseUrl + "promo-codes/", data, {
      headers: { Authorization: "Bearer " + token },
    });
    return response.data;
  },

  async updatePromoCode(
    id: number,
    data: { discount_percent?: number; bonus_months?: number; bonus_matches?: number; is_active?: boolean }
  ): Promise<any> {
    const token = tokenService.getLocalAccessToken();
    const response = await axios.patch(baseUrl + `promo-codes/${id}/`, data, {
      headers: { Authorization: "Bearer " + token },
    });
    return response.data;
  },
};
